import React, { useEffect } from 'react';
import styled from 'styled-components';

import { LIMIT_PAGE_NUM } from '../../shared/constants/limits';
import { PerPage } from '../../shared/enum/EnumRepository';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { useModalPageStore } from '../../shared/store/pageStore';
import { useModalWorkTagStore } from '../../shared/store/workTagStore';
import {
  getDefaultWorkFilterData,
  getDefaultModalWorkSortData,
} from '../../shared/store/WorkListStore';
import PageSizeStation from '../../shared/component/PageSizeStation';
import { CurrPageButton, PageButton, PageWindowButton } from './PageAboutWork';

const PageAboutWorkSelect: React.FC = () => {
  const { modalPage, resetModalPage } = useModalPageStore();
  const { getSimpleWorkListForExport } = useWorkListActions();
  const { selectedModalTags } = useModalWorkTagStore();
  const [currentWindow, setCurrentWindow] = React.useState(1);
  const [modalPageSize, setModalPageSize] = React.useState<PerPage>(PerPage.PAGE_10);

  const buildMeta = (page: number) => ({
    page,
    size: modalPageSize,
    sortData: getDefaultModalWorkSortData(),
    filterData: {
      ...getDefaultWorkFilterData(),
      tagIds: selectedModalTags.map((t) => t.id),
    },
  });

  useEffect(() => {
    getSimpleWorkListForExport(buildMeta(1));
  }, [selectedModalTags, modalPageSize]);

  useEffect(() => () => resetModalPage(), []);

  const handlePageClick = (pageNum: number) => {
    getSimpleWorkListForExport(buildMeta(pageNum));
  };

  const handlePageWindowClick = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentWindow > 1) {
      const newPage = (currentWindow - 2) * LIMIT_PAGE_NUM + 1;
      getSimpleWorkListForExport(buildMeta(newPage));
      setCurrentWindow(currentWindow - 1);
    } else if (
      direction === 'next' &&
      currentWindow < Math.ceil(modalPage.totalPages / LIMIT_PAGE_NUM)
    ) {
      const newPage = currentWindow * LIMIT_PAGE_NUM + 1;
      getSimpleWorkListForExport(buildMeta(newPage));
      setCurrentWindow(currentWindow + 1);
    }
  };

  const startPage = (currentWindow - 1) * LIMIT_PAGE_NUM + 1;
  const endPage = Math.min(startPage + LIMIT_PAGE_NUM - 1, modalPage.totalPages);

  useEffect(() => {
    const windowForCurrentPage = Math.ceil(modalPage.page / LIMIT_PAGE_NUM);
    if (windowForCurrentPage !== currentWindow) {
      setCurrentWindow(windowForCurrentPage);
    }
  }, [modalPage.page]);

  const handlePerPageChange = (value: PerPage) => {
    setModalPageSize(value);
  };

  return (
    <PageContainer>
      <PageButtonContainer>
        {currentWindow > 1 && (
          <PageWindowButton onClick={() => handlePageWindowClick('prev')}>←</PageWindowButton>
        )}
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((pageNum) =>
          pageNum === modalPage.page ? (
            <CurrPageButton key={pageNum}>{pageNum}</CurrPageButton>
          ) : (
            <PageButton key={pageNum} onClick={() => handlePageClick(pageNum)}>
              {pageNum}
            </PageButton>
          )
        )}
        {currentWindow < Math.ceil(modalPage.totalPages / LIMIT_PAGE_NUM) && (
          <PageWindowButton onClick={() => handlePageWindowClick('next')}>→</PageWindowButton>
        )}
      </PageButtonContainer>
      <PageSizeStationWrapper>
        <PageSizeStation
          currentValue={modalPageSize}
          handlePerPageChange={handlePerPageChange}
          direction={true}
        />
      </PageSizeStationWrapper>
    </PageContainer>
  );
};

export default PageAboutWorkSelect;

const PageContainer = styled.div`
  width: 100%;
  height: calc(4vh);
  min-height: 18px;
  display: flex;
  align-items: center;
  gap: 30px;
  ${({ theme }) => theme.typography.Body_04};
  transition: all 0.7s ease-in-out;
  overflow: visible;
`;

const PageButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageSizeStationWrapper = styled.div`
  overflow: visible;
  position: relative;
  z-index: 3;
`;
